import type { CustomRenderers } from '@contentful-components/rich-text/render'
import { Fragment } from 'react'
import type { ReactNode } from 'react'

const COMPLEX_PATTERNS = [
    /^#{1,6}\s/m,
    /^[-*+]\s/m,
    /^\d+\.\s/m,
    /!\[/,
    /^\|/m,
    /^```/m,
    /^>\s/m,
    /<[a-z][^>]*>/i,
]

const INLINE_PATTERN =
    /(\*\*(.+?)\*\*)|(\[([^\]]+)\]\(([^)]+)\))|(\*([^*]+?)\*)/g

// Unicode Private Use Area offset for escape placeholders
const PUA_OFFSET = 0xe000

/**
 * Replace markdown escape sequences (\* \~ \^ etc.) with Unicode Private Use
 * Area placeholders so they don't trigger inline pattern matching.
 */
function escapeMarkdownChars(text: string): string {
    return text.replace(/\\([^\w\s])/g, (_, char: string) =>
        String.fromCodePoint(PUA_OFFSET + char.charCodeAt(0)),
    )
}

/**
 * Convert PUA placeholders back to the original characters.
 */
function unescapeMarkdownChars(text: string): string {
    return text.replace(/[\uE000-\uF8FF]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - PUA_OFFSET),
    )
}

export function parseMarkdown(
    source: string,
    renderers: CustomRenderers,
): ReactNode | null {
    if (COMPLEX_PATTERNS.some((p) => p.test(source))) {
        return null
    }

    const paragraphs = source.split(/\n\n+/).filter((p) => p.trim().length > 0)

    return paragraphs.map((paragraph, i) => {
        const children = parseInline(paragraph.trim(), renderers)

        if (renderers.paragraph) {
            return (
                <Fragment key={i}>
                    {renderers.paragraph(
                        {
                            nodeType: 'paragraph',
                            data: {},
                            content: [],
                        } as any,
                        <>{children}</>,
                    )}
                </Fragment>
            )
        }

        return <p key={i}>{children}</p>
    })
}

function parseInline(text: string, renderers: CustomRenderers): ReactNode[] {
    // Normalize single newlines to spaces (markdown soft breaks).
    // The text renderer handles intentional \\n (literal backslash-n) separately.
    const normalized = text.replace(/\n/g, ' ')
    // Replace escape sequences with PUA placeholders before pattern matching
    const escaped = escapeMarkdownChars(normalized)

    const result: ReactNode[] = []
    const pattern = new RegExp(INLINE_PATTERN.source, 'g')
    let lastIndex = 0
    let match

    while ((match = pattern.exec(escaped)) !== null) {
        if (match.index > lastIndex) {
            result.push(
                ...renderText(escaped.slice(lastIndex, match.index), renderers),
            )
        }

        if (match[1]) {
            const boldContent = parseInline(match[2], renderers)
            const rendered = renderers.bold ? (
                renderers.bold(<>{boldContent}</>)
            ) : (
                <strong>{boldContent}</strong>
            )
            result.push(
                <Fragment key={`b-${match.index}`}>{rendered}</Fragment>,
            )
        } else if (match[3]) {
            const linkContent = parseInline(match[4], renderers)
            const url = unescapeMarkdownChars(match[5])
            const rendered = renderers.link ? (
                renderers.link(
                    {
                        nodeType: 'hyperlink',
                        data: { uri: url },
                        content: [],
                    } as any,
                    <>{linkContent}</>,
                )
            ) : (
                <a href={url}>{linkContent}</a>
            )
            result.push(
                <Fragment key={`l-${match.index}`}>{rendered}</Fragment>,
            )
        } else if (match[6]) {
            const italicContent = parseInline(match[7], renderers)
            const rendered = renderers.italic ? (
                renderers.italic(<>{italicContent}</>)
            ) : (
                <em>{italicContent}</em>
            )
            result.push(
                <Fragment key={`i-${match.index}`}>{rendered}</Fragment>,
            )
        }

        lastIndex = match.index + match[0].length
    }

    if (lastIndex < escaped.length) {
        result.push(...renderText(escaped.slice(lastIndex), renderers))
    }

    return result
}

function renderText(text: string, renderers: CustomRenderers): ReactNode[] {
    // Restore escaped characters before passing to the text renderer
    const unescaped = unescapeMarkdownChars(text)
    if (renderers.text) {
        const result = renderers.text(unescaped)
        return Array.isArray(result) ? result : [result]
    }
    return [unescaped]
}
